import type { ScheduleEntry } from "@edu/shared";
import { dayTitle, markNote, overlapNote } from "./common.ts";

/**
 * Почему занятие не такое, как обычно: перенос, отмена, перевод часов, особый
 * час, снятое с расписания занятие и наложения.
 *
 * Своим файлом с 03.09.2026, вместе с полосой отметок, дверью в домашку и
 * главным действием: карточка упиралась в предел (раздел 9 CLAUDE.md), а это
 * связный кусок, у которого одна работа. Читает он только строку сетки и зону,
 * состояния у него нет вовсе.
 *
 * ⚠️ **Молчаливой коррекции здесь не бывает ни одной.** Всё, что сдвинуло,
 * удвоило или отменило занятие, сказано словами: это то же правило, по которому
 * запрещено молчаливое урезание битрейта (5.5 CLAUDE.md). Человек обязан узнать
 * о переносе из интерфейса, а не из своего опоздания.
 *
 * ⚠️ Роль здесь проверяется ровно там, где решает не сервер. Предупреждений о
 * наложениях и правки занятия в ответе ученику нет вовсе, поэтому их ветки
 * молчат сами; а вот подпись особого занятия у обеих сторон РАЗНАЯ, и вот её
 * ветка по роли стоит.
 */
export function EntryNotes({ entry, zone }: { entry: ScheduleEntry; zone: string }) {
  return (
    <>
      {entry.state === "moved_away" && entry.movedTo !== null && (
        <p className="muted">
          перенесено на {dayTitle(entry.movedTo.date)}, {entry.movedTo.time}
        </p>
      )}
      {entry.movedFrom !== null && (
        <p className="muted">
          перенесено с {dayTitle(entry.movedFrom.date)}, {entry.movedFrom.time}
        </p>
      )}
      {entry.reason !== null && entry.reason !== "" && <p className="muted">причина: {entry.reason}</p>}
      {/*
        Занятие идёт не как обычно. Ученику тут только факт: из чего именно
        состоит правка (какая заготовка) ему не адресуемо, и сервер поля
        `override` в его ответе не присылает вовсе
      */}
      {entry.customized && (
        <p className="note">
          {entry.myRole === "teacher"
            ? `особое занятие${overrideText(entry)}`
            : "это занятие идёт не как обычно"}
        </p>
      )}
      {entry.shiftedFromTime !== null && (
        <p className="note">
          перевод часов: неделю назад это занятие было в {entry.shiftedFromTime}
        </p>
      )}
      {/*
        Слова про особый час живут в `common.ts` с 10.09.2026: их читает и окно
        у места падения при переносе перетаскиванием, а две редакции одной фразы
        про один и тот же двойной час человек увидел бы на соседних экранах
      */}
      {markNote(entry.mark) !== "" && <p className="note">{markNote(entry.mark)}</p>}
      {/*
        Подпись «снято с расписания» верна ТОЛЬКО для осиротевшего урока.
        Разовое занятие никто с расписания не снимал, его так и завели, и раньше
        оно получало здесь чужие слова
      */}
      {entry.source === "lesson" && !entry.standalone && (
        <p className="muted">занятие снято с расписания, урок остался</p>
      )}
      {/*
        Предупреждения приходят только преподавателю, и решает это сервер: поля
        `warnings` в ответе ученика нет вовсе. Здесь не проверяется роль
        специально, чтобы не завести второе место, где это правило можно
        забыть
      */}
      {(entry.warnings ?? []).map((w) => (
        <p className="note" key={`${w.code}${String(w.withAt)}`}>
          {overlapNote(w, zone)}
        </p>
      ))}
    </>
  );
}

/**
 * Правка занятия словами, для строки над кнопками.
 *
 * Читает только `override`, которого в ответе ученику нет вовсе, поэтому
 * вызывать её для его карточки нечего: он и не вызывается.
 */
function overrideText(entry: ScheduleEntry): string {
  const o = entry.override;
  if (o === undefined) return "";
  const parts: string[] = [];
  if (o.title !== null) parts.push(`название «${o.title}»`);
  if (o.durationMin !== null) parts.push(`${String(o.durationMin)} мин`);
  if (o.planId !== null) parts.push(`заготовка «${o.planTitle ?? "убрана"}»`);
  return parts.length === 0 ? "" : `: ${parts.join(", ")}`;
}
